/**
 * Every LogRocket custom event the app sends, and why each exists. Send one
 * with `track()` (`services/telemetry.ts`), the only module that talks to
 * LogRocket; an ESLint rule keeps it that way.
 *
 * An event is declared twice, side by side: its payload in
 * `TelemetryPayloads`, and its definition in `TELEMETRY_EVENTS`:
 *   - `ticket`: the ticket that owns it
 *   - `kind`: `diagnostic` (a failure ops watch for) or `usage` (how a
 *     feature is used)
 *   - `question`: what the event answers
 *   - `retireWhen`: when to delete it, or why it stays
 *   - `enabled`: `false` pauses it; call sites stay, nothing is sent
 *   - `onceKey`: sent once per page load per key; omitted, every call is sent
 *
 * Payload values are flat (string, number or boolean) so each field is one
 * filterable property in LogRocket: join a list into one string. `track()`
 * adds `scopeRole` and `scopeEntityId`, the role and account this tab is
 * looking at when the event is sent, unless the payload sets them itself.
 * Keep events low-volume: LogRocket caps a session at 2,000 event properties
 * and silently drops what comes after, so something that fires per render is
 * a `breadcrumb()`, or gets a `onceKey`.
 *
 * To stop tracking something, pause it with `enabled: false`, or retire it by
 * deleting both declarations; the compiler then lists every call site.
 * The event names are what LogRocket dashboards and filters match on, so
 * renaming one splits its history.
 */
export type TelemetryPayload = Record<string, string | number | boolean>;

export interface TelemetryEvent<P extends TelemetryPayload> {
  /** The ticket that owns the event. */
  ticket: string;
  kind: 'diagnostic' | 'usage';
  /** The question the event answers. */
  question: string;
  /** When to delete it, or why it stays. */
  retireWhen: string;
  /** `false` pauses the event: its call sites stay, nothing is sent. */
  enabled: boolean;
  /** Sends the event once per page load per key. Omitted: every call is sent. */
  onceKey?: (payload: P) => string;
}

export interface TelemetryPayloads {
  'assets-downloaded': {
    /** `fullSize` (Large), `1920w` (Medium) or `830w` (Small). */
    size: string;
    watermark: boolean;
    /** `4:3` when cropped; `original` is the stored files as they are, whatever their shape. */
    aspectRatio: string;
    /** Photos named in the request; 0 for a download by order (`byOrder`). */
    photoCount: number;
    /** By order tag, with no photo list: the Orders list's fallback (DV-2763). */
    byOrder: boolean;
    isDownloadAll: boolean;
    includeHidden: boolean;
  };
  /** A guard's access-denial snapshot, captured to storage and sent after init. */
  'access-denied-redirect': Record<string, string>;
  /** The same snapshot, captured by the `/error` page as it renders. */
  'access-denied-page-render': Record<string, string>;
  'entity-scope-header-suppressed': {
    reason: string;
    /** A USER id. Events from before DV-3573 carried an entity id as `claim`. */
    tokenSub: string;
    selected: string;
    ageMs: number;
  };
  'floor-plan-offer-degraded':
    | { reason: string; missingTierSlugs: string }
    | { reason: 'eligibility-undecided'; entityUUID: string; cause: string };
  'photoset-not-found': {
    propertyUUID: string;
    photosetUUID: string;
    /** The property's photosets, newest first; empty when it has none. */
    loadedPhotosetIds: string;
  };
  'upload-discard-problem': { kind: string } & Record<string, string>;
}

export type TelemetryEventName = keyof TelemetryPayloads;

const ACCESS_DENIAL = {
  ticket: 'DV-3012',
  kind: 'diagnostic',
  question:
    'How often does a guard send a user to /error, and from which guard ' +
    '(the snapshot holds the role, scope and URL at the moment of denial)?',
  retireWhen:
    "DV-3012's BC-8 has shipped and the resolveEntityScope-sourced count has stayed " +
    'at rest for one full release cycle (util/access-denial-log.ts lists what to delete).',
  enabled: true
} as const;

export const TELEMETRY_EVENTS: {
  [N in TelemetryEventName]: TelemetryEvent<TelemetryPayloads[N]>;
} = {
  'assets-downloaded': {
    ticket: 'DV-3757',
    kind: 'usage',
    question:
      'How often are photos downloaded at each size (Large, Medium, Small), with which ' +
      'options, and by customers or internal staff (scopeRole)?',
    retireWhen: 'Product has decided which download sizes to offer and which comes first.',
    enabled: true
  },
  'access-denied-redirect': ACCESS_DENIAL,
  'access-denied-page-render': ACCESS_DENIAL,
  'entity-scope-header-suppressed': {
    ticket: 'DV-3421',
    kind: 'diagnostic',
    question:
      'Does a tab keep sending requests without X-Entity-UUID after another user took ' +
      'over the token, past the reload window?',
    retireWhen:
      'Kept: the only signal for a tab that never reloaded after a user change ' +
      '(services/entity-scope-header.ts). It reports once per user and selection itself.',
    enabled: true
  },
  'floor-plan-offer-degraded': {
    ticket: 'DV-3204',
    kind: 'diagnostic',
    question:
      'Which carts could not show the floor plan tier choice in full, and why: a catalog ' +
      'failure, paid tiers the catalog cannot describe, or eligibility order-service ' +
      'could not decide (DV-3697)?',
    retireWhen:
      'The tier content comes from the catalog and the rollout covers every account ' +
      '(both banked in docs/engineering-backlog.md).',
    enabled: true,
    // The hook runs on the cart page and again in every slider opened, and one
    // event says as much as fifty. Undecided eligibility is once per account,
    // whatever the cause: a read that fails, then answers null, is one account.
    onceKey: (payload) =>
      'entityUUID' in payload
        ? `${payload.reason}|${payload.entityUUID}`
        : `${payload.reason}|${payload.missingTierSlugs}`
  },
  'photoset-not-found': {
    ticket: 'DV-2849',
    kind: 'diagnostic',
    question:
      'How often does an internal workspace route name a photoset the property does not ' +
      'have, and for which properties: a dangling order link, or a set on another property?',
    retireWhen: 'The post-release counts have been reviewed and the cause fixed or accepted.',
    enabled: true
  },
  'upload-discard-problem': {
    ticket: 'DV-2646',
    kind: 'diagnostic',
    question:
      'Which discards of cancelled uploads fail or are refused, and why? An upload that is ' +
      'not discarded comes back uncaptioned with the next Legacy re-send (PR #1205).',
    retireWhen: 'Kept while cancel relies on asset-service discarding what was uploaded.',
    enabled: true
  }
};
